import { skipToken } from "@reduxjs/toolkit/query";
import type Guid from "_shared/guids/guid";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { type PermissionTarget, useGetPermissionTargetsByIdsQuery } from "permissions/_api/permission-targets-api-service";
import { useGetEveryPermissionQuery } from "permissions/_api/permissions-api-service";
import { grantOrigin, groupRecordGrants } from "permissions/_views/people/person-record-grants-model";
import { recordQuery } from "permissions/_views/people/use-person-record-grants";
import useRecordNameSearch from "permissions/_views/people/use-record-name-search";
import { DIRECT } from "permissions/_views/wording/permission-wording";
import Permission from "permissions/permission";
import { useMemo } from "react";

/** One record Add access offers as a target, and the person's own grant on it, if they hold one. */
export type RecordTarget = PermissionTarget &
  Readonly<{
    /** The live direct grant the person holds on the record: choosing the record changes it. */
    held?: Permission;
  }>;

export type RecordTargetSearch = Readonly<{
  /**
   * The records whose names match and that may be granted, newest first, or undefined while there is
   * no search or no answer.
   */
  targets?: readonly RecordTarget[];
  isSearching: boolean;
  error?: string;
}>;

/**
 * The largest page the Permissions API serves. The held read pages through every grant on the found
 * records: a record carries derived grants beside its direct one, so on any one page the direct grant
 * can be missing, and Grant would post a second one (#6837).
 */
const HELD_PAGE_SIZE = 100;

/**
 * The live direct grant a record's grants include. A grant derived from another — a conversation
 * grant Messaging minted, a proposal grant from taking part — is changed where it came from, so a
 * record held only that way is offered for a direct grant of its own.
 */
function directGrant(grants: readonly Permission[]): Permission | undefined {
  return grants.find((grant) => !grant.isDisabled && grantOrigin(grant, []) === DIRECT);
}

/**
 * The target step of Add access: records of a per-record kind found by name, each named, and marked
 * when the person already holds a direct grant on it.
 *
 * Records are found through the kind's admin-gated targets search (`useRecordNameSearch`, #6795), so
 * the step offers a record the administrator cannot open as well. A record the names read then answers
 * as missing is left out.
 */
export default function useRecordTargetSearch(userId: Guid, kind: PermissionKind | undefined, text: string): RecordTargetSearch {
  const resourceType = kind?.perRecord ? kind.resourceType : undefined;
  const search = useRecordNameSearch(resourceType, text);
  const ids = search.ids;
  // Memoized on purpose: it reaches the held-grants query's dependencies below.
  const found = useMemo(
    () => (kind && resourceType && ids && ids.length > 0 ? { kind, resourceType, ids } : undefined),
    [kind, resourceType, ids],
  );

  const names = useGetPermissionTargetsByIdsQuery(found ? { resourceType: found.resourceType, ids: found.ids } : skipToken);

  // Memoized on purpose: the query is a class instance that reaches RTK Query's arguments.
  const heldQuery = useMemo(
    () => (found ? recordQuery(userId, found.kind, "live", 0, found.ids, HELD_PAGE_SIZE) : undefined),
    [userId, found],
  );
  const heldList = useGetEveryPermissionQuery(heldQuery ? { query: heldQuery } : skipToken);

  const targets = useMemo(() => {
    if (!kind || !ids) return undefined;
    if (ids.length === 0) return [];
    if (!names.currentData || !heldList.currentData) return undefined;

    const nameOf = new Map(names.currentData.found.map((target) => [target.id.toLowerCase(), target.name]));
    const heldOn = new Map(
      groupRecordGrants(kind, Permission.fromJSONArray(heldList.currentData)).map((row) => [
        row.targetId.toLowerCase(),
        directGrant(row.grants),
      ]),
    );
    // A record the names answer lists as unavailable, missing or refused, is left out: a grant on it
    // would be refused.
    return ids.flatMap((id) => {
      const name = nameOf.get(id.toLowerCase());
      return name === undefined ? [] : [{ id, name, held: heldOn.get(id.toLowerCase()) }];
    });
  }, [kind, ids, names.currentData, heldList.currentData]);

  const failure = search.error ?? names.error ?? heldList.error;
  return {
    targets: failure ? undefined : targets,
    isSearching: search.isSearching || names.isFetching || heldList.isFetching,
    error: failure === undefined ? undefined : typeof failure === "string" ? failure : (getErrorMessageFromUnknown(failure) ?? "The search failed."),
  };
}
